import { PlatformSiteAri } from '@atlassian/ari';
import { EntryPointRef } from '@atlassian/entry-point-types';
import { starredQueryBuilder } from '@atlassian/home-starred/cypher-query';
import { starredListEntryPoint } from '@atlassian/home-starred/entrypoint';
import { loadEntryPoint } from 'react-relay';

import { getDefaultTabKey } from '@townsquare/hometab-saved-tab';
import { getAggRelayEnvironment } from '@townsquare/relay-utils/agg-environment';
import { createResource } from '@townsquare/router/resources/createResource';
import type { RouteResource } from '@townsquare/router/routing-types';
import type { ResourceContext } from '@townsquare/router/types';
import { expVal } from '@townsquare/stat-sig';
import { fg } from '@townsquare/stat-sig/gate';

import { APP_QUERY_PARAM, parseAppParam_platform } from '../types';

const isStarredTabInitially = (): boolean => {
	return getDefaultTabKey() === 'starred';
};

type PlatformStarredData = EntryPointRef<typeof starredListEntryPoint>;

export const platformStarredFeedResource: RouteResource<PlatformStarredData | undefined> =
	createResource<PlatformStarredData | undefined>({
		type: 'STARRED',
		getKey: () => 'platform-starred-resource',
		getData: async (routerContext, resourceContext) => {
			if (!fg('townsquare_platform_starred_list')) {
				return;
			}

			const { workspace } = resourceContext as ResourceContext;

			if (!workspace?.cloudId) {
				return;
			}

			const isStarredPage = routerContext.route.name === 'starred';

			const isYourWorkPage =
				routerContext.route.name === 'your-work' &&
				routerContext.match.params.tabName === 'starred' &&
				fg('townsquare_your_work_page');

			const isHometabStarred =
				routerContext.route.name === 'hometab' &&
				isStarredTabInitially() &&
				expVal('townsquare_starred_fyp', 'isEnabled', false);

			if (!isStarredPage && !isHometabStarred && !isYourWorkPage) {
				return;
			}

			let collabContextId;

			try {
				collabContextId = PlatformSiteAri.create({ siteId: workspace.cloudId }).toString();
			} catch {
				return;
			}

			const apps = parseAppParam_platform(routerContext.query[APP_QUERY_PARAM]);

			const reference = loadEntryPoint(
				{ getEnvironment: getAggRelayEnvironment },
				starredListEntryPoint,
				{
					cypherQuery: starredQueryBuilder(apps),
					queryContext: collabContextId,
				},
			);

			return reference;
		},
	});
